'use client'

/**
 * Public header Search — catalog MorphingSearch (beUI), not a house invent.
 * Client-open morph; suggest feed for instant places; ⌘K / Ctrl+K toggles it.
 * SITE-110 SearchMorph pattern, sized for chrome (icon on phone, pill on desk).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { Search } from 'lucide-react'
import type { MorphingSearch as MorphingSearchComponent, MorphingSearchItem } from '@/components/motion/morphing-search'
import { flattenSuggestions, useSearchSuggest } from '@/components/search/SearchSuggest'
import { trackSiteSearch } from '@/components/search/track-site-search'
import './V3MorphSearch.css'

// MorphingSearch (and Framer Motion under it) is not in the first load.
// The header is on every page, so a static import put ~70 KB of motion code
// ahead of every hero image (search-ai P10 speed). The bar paints a still
// copy of the closed trigger; the morph loads after the window load event,
// or at once when a visitor reaches for it (pointer, focus, tap, Cmd+K).
// A failed chunk download clears the cache, so the next reach retries.
type MorphingSearchType = typeof MorphingSearchComponent
let morphLoad: Promise<MorphingSearchType> | null = null
function loadMorphingSearch(): Promise<MorphingSearchType> {
  morphLoad ??= import('@/components/motion/morphing-search')
    .then((mod) => mod.MorphingSearch)
    .catch((error: unknown) => {
      morphLoad = null
      throw error
    })
  return morphLoad
}

/** The closed trigger, still or morph: the icon button or the pill's input. */
function focusTrigger(wrapper: HTMLElement | null) {
  wrapper
    ?.querySelector<HTMLElement>('button[data-v3-morph="trigger"], [data-v3-morph="trigger"] input')
    ?.focus()
}

/**
 * The closed trigger before the morph loads: the same box, stroke, icon and
 * aria as MorphingSearch's closed state (components/motion/morphing-search.tsx),
 * so the swap moves nothing. Reaching for it loads the morph and opens it.
 */
function ChromeSearchStill({
  iconOnly,
  placeholder,
  className,
  typed,
  onType,
  onWarm,
  onOpen,
  onTap,
  onCancel,
}: {
  iconOnly: boolean
  placeholder: string
  className: string
  typed: string
  onType: (text: string) => void
  onWarm: () => void
  onOpen: () => void
  onTap: () => void
  onCancel: (clearTyped: boolean, focusTo: EventTarget | null) => void
}) {
  const trigger = 'flex size-full items-center rounded-xl bg-background text-left backdrop-blur-md outline-none [--search-trigger-stroke:var(--color-border)] hover:[--search-trigger-stroke:var(--color-border-strong)]'
  const stroke = { boxShadow: 'inset 0 0 0 1px var(--search-trigger-stroke)' }
  return (
    <div
      className={`relative ${iconOnly ? 'size-12' : 'h-12 w-72 max-w-full'} ${className}`}
      onPointerEnter={onWarm}
      onTouchStart={onWarm}
    >
      {iconOnly ? (
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded="false"
          aria-label={placeholder}
          data-v3-morph="trigger"
          onFocus={onWarm}
          onClick={onTap}
          style={stroke}
          className={`${trigger} cursor-pointer justify-center focus-visible:ring-2 focus-visible:ring-ring`}
        />
      ) : (
        <div data-v3-morph="trigger" style={stroke} className={`${trigger} focus-within:ring-2 focus-within:ring-ring`}>
          <input
            data-clarity-mask="True"
            type="search"
            role="combobox"
            aria-label={placeholder}
            aria-haspopup="dialog"
            aria-expanded="false"
            placeholder={placeholder}
            autoComplete="off"
            value={typed}
            className="size-full cursor-text bg-transparent py-0 pl-9 pr-14 text-base sm:text-sm text-foreground outline-none placeholder:text-muted-foreground"
            onFocus={onOpen}
            // Keys typed before the morph mounts are kept and handed to it.
            onChange={(event) => {
              onType(event.target.value)
              onOpen()
            }}
            // Leaving the pill or pressing Escape while the morph loads calls
            // off the open, so the dialog never pops up seconds later, and
            // drops what was typed so it does not come back on the next open.
            onBlur={(event) => onCancel(true, event.relatedTarget)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') onCancel(true, null)
            }}
          />
        </div>
      )}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 flex items-center ${iconOnly ? 'justify-center' : 'gap-2.5 px-3.5'}`}
      >
        <Search className="size-4 shrink-0 text-muted-foreground" />
      </div>
    </div>
  )
}

const PLACE_SEEDS: MorphingSearchItem[] = [
  { id: '/homes-for-sale/bend', title: 'Bend', description: 'City' },
  { id: '/homes-for-sale/redmond', title: 'Redmond', description: 'City' },
  { id: '/homes-for-sale/sisters', title: 'Sisters', description: 'City' },
  // Sunriver is a registry resort community that is its own city: its one
  // inventory page is /communities/sunriver, and /homes-for-sale/sunriver
  // canonicals there (lib/communities/self-city-community.ts). A seed never
  // opens a search that points its canonical somewhere else.
  { id: '/communities/sunriver', title: 'Sunriver', description: 'Community' },
  { id: '/communities/tetherow', title: 'Tetherow', description: 'Bend' },
  { id: '/homes-for-sale/prineville', title: 'Prineville', description: 'City' },
  { id: '/homes-for-sale/la-pine', title: 'La Pine', description: 'City' },
  { id: '/homes-for-sale/madras', title: 'Madras', description: 'City' },
]

export function V3ChromeSearch() {
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [iconOnly, setIconOnly] = useState(true)
  const { suggestions } = useSearchSuggest(query)
  const [Morph, setMorph] = useState<MorphingSearchType | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const refocusTrigger = useRef(false)
  const [pendingOpen, setPendingOpen] = useState(false)
  // pendingOpen for event handlers and promise callbacks, which see stale state.
  const pendingRef = useRef(false)
  // A real input that holds focus (and the iOS keyboard) between a tap on the
  // icon and the morph's own input. It stays mounted across the swap.
  const holderRef = useRef<HTMLInputElement>(null)
  // What a visitor typed into the still pill while the morph loaded.
  const [typed, setTyped] = useState('')

  const loadMorph = useCallback(() => {
    void loadMorphingSearch().then(
      (component) => {
        // The swap unmounts the still trigger. If it had keyboard focus, the
        // morph's trigger takes it, or Enter lands on <body> and does nothing.
        // An open on its way hands focus to the holder instead, so the
        // keyboard stays up until the morph's input takes it.
        const active = document.activeElement
        const inside = wrapperRef.current?.contains(active) ?? false
        if (pendingRef.current && inside && active !== holderRef.current) holderRef.current?.focus()
        else refocusTrigger.current = inside && active !== holderRef.current
        setMorph(() => component)
      },
      // Failed download: drop the pending open and what was typed for it; the
      // next reach retries. Focus parked on the holder goes back to the trigger.
      () => {
        pendingRef.current = false
        setPendingOpen(false)
        setTyped('')
        if (document.activeElement === holderRef.current) focusTrigger(wrapperRef.current)
      },
    )
  }, [])

  const cancelOpen = useCallback((clearTyped: boolean, focusTo: EventTarget | null) => {
    if (!pendingRef.current) return
    // Focus moving between the pill, the holder and the morph is the open
    // itself, not the visitor leaving.
    if (focusTo instanceof Node && wrapperRef.current?.contains(focusTo)) return
    pendingRef.current = false
    setPendingOpen(false)
    if (clearTyped) setTyped('')
  }, [])

  useEffect(() => {
    if (!Morph || !refocusTrigger.current) return
    refocusTrigger.current = false
    // Icon mode only. A pill that had focus is already opening via
    // pendingOpen, and the open morph focuses its own input, carrying `typed`.
    wrapperRef.current?.querySelector<HTMLElement>('button[data-v3-morph="trigger"]')?.focus()
  }, [Morph])

  const openMorph = useCallback(() => {
    // The morph measures its trigger on mount and draws its panel from that
    // box. Mounted already open, the box is 0 wide and the panel never shows,
    // so it mounts closed and opens on the next commit.
    loadMorph()
    pendingRef.current = true
    setPendingOpen(true)
  }, [loadMorph])

  const tapOpen = useCallback(() => {
    // iOS raises the keyboard only for a focus() inside the tap. The morph's
    // input mounts after an async import, too late, so a real input takes
    // focus now and the morph's input takes it from there.
    holderRef.current?.focus()
    openMorph()
  }, [openMorph])

  useEffect(() => {
    if (!Morph || !pendingOpen) return
    // The second commit is the point: the morph has measured its trigger.
    pendingRef.current = false
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPendingOpen(false)
    setOpen(true)
  }, [Morph, pendingOpen])

  useEffect(() => {
    // After the load event and an idle frame: the hero image has painted, so
    // the morph's code never competes with it for the first bytes.
    let idle: number | undefined
    let timer: ReturnType<typeof setTimeout> | undefined
    const schedule = () => {
      if (typeof window.requestIdleCallback === 'function') {
        idle = window.requestIdleCallback(loadMorph, { timeout: 3000 })
      } else {
        timer = setTimeout(loadMorph, 1500)
      }
    }
    if (document.readyState === 'complete') schedule()
    else window.addEventListener('load', schedule, { once: true })
    return () => {
      window.removeEventListener('load', schedule)
      if (idle !== undefined) window.cancelIdleCallback(idle)
      if (timer !== undefined) clearTimeout(timer)
    }
  }, [loadMorph])

  useEffect(() => {
    // Icon-only through the whole 1280-class bar (V3Chrome.css: 56.25rem–
    // 84.99rem), not just below the nav breakpoint. A compressed pill in
    // that range clipped the placeholder to "Sear" and ran the phone off
    // 1024 (SITE-155). The icon trigger is the tap floor (~44px).
    const mq = window.matchMedia('(max-width: 84.99rem)')
    const apply = () => setIconOnly(mq.matches)
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  const onOpenChange = useCallback((next: boolean) => {
    setOpen(next)
    if (!next) {
      setQuery('')
      setTyped('')
    }
  }, [])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return
      if (event.key.toLowerCase() !== 'k') return
      event.preventDefault()
      if (open) onOpenChange(false)
      else openMorph()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, openMorph, onOpenChange])

  const items = useMemo(() => {
    // Comprehensive (Matt 2026-09-16): every kind the suggest feed returns —
    // addresses, cities, subdivisions, neighborhoods, ZIPs, brokers, reports and
    // guides — not a hand-picked five. The feed already matched them to the
    // query; `keywords` carries that query so the morph's own substring filter
    // cannot drop a match the server made on a field the row does not print.
    const typed = flattenSuggestions(suggestions).map((item) => ({
      id: item.href,
      title: item.label,
      description: item.sublabel,
      keywords: [query],
    }))
    return typed.length > 0 ? typed : PLACE_SEEDS
  }, [suggestions, query])

  const onSelect = useCallback(
    (item: MorphingSearchItem) => {
      // GA4 and the first-party store both hear it (track-site-search.ts). Before
      // 2026-10-14 this sent GA4 only, so the admin search card never saw it.
      trackSiteSearch({ surface: 'chrome', source: 'typeahead', query: item.title, term: query })
      setOpen(false)
      setQuery('')
      setTyped('')
      router.push(item.id)
    },
    [router, query],
  )

  return (
    <div ref={wrapperRef} className="v3-chrome__search">
      <input
        ref={holderRef}
        type="search"
        tabIndex={-1}
        // Not aria-hidden: a screen reader tapping before the preload lands
        // here, and a hidden or unlabeled field reads as silence.
        aria-label="Search homes, places…"
        autoComplete="off"
        data-clarity-mask="True"
        value={typed}
        onChange={(event) => setTyped(event.target.value)}
        onBlur={(event) => cancelOpen(true, event.relatedTarget)}
        onKeyDown={(event) => {
          if (event.key !== 'Escape') return
          cancelOpen(true, null)
          focusTrigger(wrapperRef.current)
        }}
        className="pointer-events-none absolute left-0 top-0 size-px opacity-0 text-base"
      />
      {Morph ? (
        <Morph
          items={items}
          placeholder="Search homes, places…"
          iconOnly={iconOnly}
          open={open}
          onOpenChange={onOpenChange}
          onQueryChange={setQuery}
          openQuery={typed}
          onSelect={onSelect}
          shortcut=""
          emptyMessage="No places match that."
          className="v3-chrome__search-morph"
          // The chrome is sticky at z-index 100 (V3Chrome.css); the morph's
          // portal defaults to z-50, which put the open input row behind the
          // header on every page (Matt, phone, 2026-09-16). Above the chrome and
          // the Find-me stage (120), below the nav overlay (200).
          overlayClassName="z-[150]"
        />
      ) : (
        <ChromeSearchStill
          iconOnly={iconOnly}
          placeholder="Search homes, places…"
          className="v3-chrome__search-morph"
          typed={typed}
          onType={setTyped}
          onWarm={loadMorph}
          onOpen={openMorph}
          onTap={tapOpen}
          onCancel={cancelOpen}
        />
      )}
    </div>
  )
}
